import { useMemo } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import type { StudioId } from '@/domain/types';
import InfoTip from '@/components/InfoTip';
import { ReportBarChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import ReportHeatmap, { type HeatmapColumn, type HeatmapRowData } from '@/components/reporting/ReportHeatmap';
import { useFlashHighlight } from '@/hooks/useFlashHighlight';
import { aud } from '@/utils/format';
import {
  funnel,
  campaignPerformance,
  newLeadsBySourceByDay,
  inquiryArrivalHeatmap,
} from '@/domain/reportingEngine';

function dayLabel(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00+10:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' });
}

function hourLabel(hour: number): string {
  const period = hour < 12 ? 'a' : 'p';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}${period}`;
}

interface Props {
  /** The selected date-range window for the leads chart + funnel. */
  days: readonly string[];
  /** Studio scope, or undefined for all studios. */
  studioId?: StudioId;
}

/**
 * Leads & marketing tab (Prompt F, Stage 4). New leads by day and source, funnel
 * conversion per step, cost per booked deposit per campaign, and the
 * inquiry-arrival heatmap (the staffing signal).
 */
export default function LeadsTab({ days, studioId }: Props) {
  const customers = useBrainStore((s) => s.customers);
  const deposits = useBrainStore((s) => s.deposits);
  const bookings = useBrainStore((s) => s.bookings);
  const marketingCampaigns = useBrainStore((s) => s.marketingCampaigns);

  const flashing = useFlashHighlight(['stripe-webhook-success']);

  const sourceTrend = useMemo(() => {
    const rows = newLeadsBySourceByDay(days, studioId);
    return rows.map((r) => ({ day: dayLabel(r.dayIso), Web: r.web, Social: r.social, Campaign: r.campaign }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, studioId, customers]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const funnelSteps = useMemo(() => funnel(days, studioId), [days, studioId, customers, deposits, bookings, marketingCampaigns]);
  // Campaigns are attributed by the paying customer's campaign, not by studio, so this stays all-studios.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const campaigns = useMemo(() => campaignPerformance(), [marketingCampaigns, deposits, customers]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const arrival = useMemo(() => inquiryArrivalHeatmap(studioId), [studioId, customers]);

  // Conversion between consecutive count steps (spend step excluded).
  const conversions = useMemo(() => {
    const countSteps = funnelSteps.filter((s) => s.kind === 'count');
    const out: Array<{ from: string; to: string; pct: number | null; toValue: number }> = [];
    for (let i = 1; i < countSteps.length; i += 1) {
      const prev = countSteps[i - 1]!;
      const step = countSteps[i]!;
      out.push({
        from: prev.stage,
        to: step.stage,
        pct: prev.value > 0 ? (step.value / prev.value) * 100 : null,
        toValue: step.value,
      });
    }
    return out;
  }, [funnelSteps]);

  const campaignChart = campaigns
    .filter((c) => c.costPerDepositAud !== null)
    .map((c) => ({ name: c.campaign.name, Cost: Math.round(c.costPerDepositAud!) }));

  // Only render the hours that actually carry inquiries (clamped to a business
  // window) so the heatmap stays compact without silently dropping arrivals.
  const { minHour, maxHour } = useMemo(() => {
    let lo = 24;
    let hi = -1;
    for (const row of arrival) {
      row.hours.forEach((count, hour) => {
        if (count > 0) {
          lo = Math.min(lo, hour);
          hi = Math.max(hi, hour);
        }
      });
    }
    if (hi < 0) return { minHour: 8, maxHour: 20 };
    return { minHour: Math.min(lo, 8), maxHour: Math.max(hi, 20) };
  }, [arrival]);

  const hourColumns: HeatmapColumn[] = [];
  for (let h = minHour; h <= maxHour; h += 1) hourColumns.push({ key: `h${h}`, label: hourLabel(h) });
  const arrivalRows: HeatmapRowData[] = arrival.map((row) => ({
    label: row.label,
    cells: hourColumns.map((_, i) => {
      const hour = minHour + i;
      return { colKey: `h${hour}`, value: row.hours[hour] ?? 0, title: `${row.label} ${hourLabel(hour)}: ${row.hours[hour] ?? 0} inquiries` };
    }),
  }));

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-leads">
      {/* New leads by day + source */}
      <section className="panel p-4" data-testid="leads-by-source">
        <h2 className="heading-serif text-lg text-cream mb-1">
          New leads by source
          <InfoTip>
            New leads by first-touch day, grouped by where they came from: paid campaign (has a campaign
            attribution), social DM (Meta/Instagram), or web/direct (everything else). Over the selected range.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Web · social DM · paid campaign, per day this week
        </p>
        <ReportBarChart
          data={sourceTrend}
          xKey="day"
          stacked
          series={[
            { key: 'Web', name: 'Web / direct', color: REPORT_COLORS.azure },
            { key: 'Social', name: 'Social DM', color: REPORT_COLORS.gold },
            { key: 'Campaign', name: 'Paid campaign', color: REPORT_COLORS.mint },
          ]}
        />
      </section>

      {/* Funnel conversion per step */}
      <section className="panel p-4" data-testid="leads-funnel-conversion">
        <h2 className="heading-serif text-lg text-cream mb-3">
          Funnel conversion, this period
          <InfoTip>
            Step-to-step conversion for the selected span: inquiries → qualified leads → deposits paid → bookings
            completed. Each tile is the later step ÷ the earlier step.
          </InfoTip>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {conversions.map((c) => (
            <div key={`${c.from}-${c.to}`} className="panel-dark p-4" data-testid={`leads-conv-${c.to}`}>
              <p className="label-small-caps">
                {c.from} → {c.to}
              </p>
              <p className={clsx('font-cinzel text-3xl mt-2', c.pct === null ? 'text-cream-faint' : 'text-gold-bright')}>
                {c.pct === null ? '—' : `${c.pct.toFixed(0)}%`}
              </p>
              <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                {c.toValue} {c.to.toLowerCase()}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Cost per booked deposit per campaign */}
      <section className={clsx('panel p-4', flashing && 'report-flash')} data-testid="leads-campaigns">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Cost per booked deposit
          <InfoTip>
            Per campaign: seeded spend ÷ deposits attributed to it (via the paying customer&apos;s campaign). The
            cost-per-booked-deposit number, computed from the attributed deposits instead of counted by hand. Dash =
            none converted yet.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Seeded spend ÷ deposits attributed, per campaign
        </p>
        {campaignChart.length > 0 ? (
          <ReportBarChart
            data={campaignChart}
            xKey="name"
            hideLegend
            series={[{ key: 'Cost', name: 'Cost / deposit', color: REPORT_COLORS.gold }]}
          />
        ) : (
          <p className="font-serif text-cream-soft text-sm">No campaign deposits attributed yet.</p>
        )}
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Campaign</th>
                <th className="text-right px-2 py-2">Spend</th>
                <th className="text-right px-2 py-2">Messages</th>
                <th className="text-right px-2 py-2">Deposits</th>
                <th className="text-right px-2 py-2">Cost / deposit</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.campaign.id} className="border-t border-gold-dim/15" data-testid={`leads-campaign-${c.campaign.id}`}>
                  <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{c.campaign.name}</td>
                  <td className="px-2 py-2 text-right font-mono text-cream-soft">{aud(c.spendAud)}</td>
                  <td className="px-2 py-2 text-right font-mono text-cream-soft">{c.messagesSent}</td>
                  <td className="px-2 py-2 text-right font-mono text-cream">{c.deposits}</td>
                  <td className={clsx('px-2 py-2 text-right font-mono', c.costPerDepositAud === null ? 'text-cream-faint' : 'text-gold-bright')}>
                    {c.costPerDepositAud === null ? '—' : aud(c.costPerDepositAud)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Inquiry-arrival heatmap */}
      <section className="panel p-4" data-testid="leads-arrival-heatmap">
        <h2 className="heading-serif text-lg text-cream mb-1">
          When inquiries land
          <InfoTip>
            First-touch count by weekday × hour of day, all-time. Darker cells = more inquiries arriving then — a
            staffing signal for when to have someone on the phones.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          First touch by weekday × hour — a staffing signal
        </p>
        <ReportHeatmap
          columns={hourColumns}
          rows={arrivalRows}
          tone="gold"
          caption="Darker = more inquiries arriving in that hour"
          emptyLabel="No inquiries recorded yet."
        />
      </section>
    </div>
  );
}
